import {CodeView} from "./code";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {
    useBarcodeListener,
    useDeactivateListener,
    useDevicePresent,
    useOccupancies,
    useStorageItemValue
} from "@variocube/cube-ui";
import {Category, Product, Shop} from "./types";
import {CategoriesView} from "./categories";
import {ProductsView} from "./products";
import {ProductView} from "./product";
import {VendView} from "./vend";
import {ErrorView} from "./error";
import {AdminProductsFlow} from "./admin/AdminProductsFlow";
import {Inventory} from "./inventory";
import {Barcode, DeviceType, Occupancy} from "@variocube/app-api";
import {PickupView} from "./pickup";
import {VendInShop} from "./VendInShop";
import {useLocalization} from "../i18n";

export function App() {
    const {t} = useLocalization();
    const shop = useStorageItemValue<Shop>("shop");
    const categories = useStorageItemValue<Category[]>("categories");
    const unsortedProducts = useStorageItemValue<Product[]>("products") || [];
    const occupancies = useOccupancies();

    const products = useMemo(() => unsortedProducts.sort((a, b) => a.name.localeCompare(b.name)), [unsortedProducts]);

    const inventory = useMemo(() => new Inventory(products, occupancies), [products, occupancies]);

    const paymentTerminalPresent = useDevicePresent(DeviceType.PaymentTerminal);

    const [code, setCode] = useState<boolean>(false);
    const [selectedShop, setSelectedShop] = useState<Shop>();
    const [selectedCategory, setSelectedCategory] = useState<Category>();
    const [selectedProduct, setSelectedProduct] = useState<Product>();
    const [checkout, setCheckout] = useState<boolean>(false);
    const [pickup, setPickup] = useState<Occupancy>();

    const handleDeactivate = useCallback(async () => {
        setSelectedCategory(undefined);
        setSelectedShop(undefined);
        setSelectedProduct(undefined);
    }, []);
    useDeactivateListener(handleDeactivate);

    const handlePickupOrder = useCallback(() => setCode(true), []);
    const handleCodeCancel = useCallback(() => setCode(false), []);

    const handleProductBack = useCallback(() => setSelectedProduct(undefined), []);
    const handleCategoryBack = useCallback(() => setSelectedCategory(undefined), []);

    const handleCode = useCallback(async (code: string) => {
        const occupancy = occupancies.find(occupancy => occupancy.accessCode == code);
        if (occupancy) {
            setPickup(occupancy);
            setCode(false);
        }
        else if (shop && shop.accessCode === code) {
            setSelectedShop(shop);
            setCode(false);
        }
        else {
            throw new Error("Invalid code.");
        }
    }, [shop, occupancies]);

    const handleVendProduct = useCallback((product: Product) => {
        if (inventory.getCount(product) > 0) {
            setCheckout(true);
        }
    }, [inventory]);

    const handleVendDone = useCallback(() => {
        setCheckout(false);
        setSelectedProduct(undefined);
        setSelectedCategory(undefined);
    }, []);

    const handleShopDone = useCallback(() => setSelectedShop(undefined), []);

    const handlePickupDone = useCallback(() => setPickup(undefined), []);

    const handleBarcode = useCallback(async (code: Barcode) => {
        await handleCode(atob(code.code));
    }, [handleCode]);
    useBarcodeListener(handleBarcode);


    if (!categories || !products || categories.length == 0) {
        return (
            <ErrorView message={t("ErrorNotConfigured")}/>
        );
    }
    if (pickup) {
        return (
            <PickupView occupancy={pickup} onDone={handlePickupDone}/>
        )
    }
    if (code) {
        return (
            <CodeView onCode={handleCode} onCancel={handleCodeCancel}/>
        );
    }
    if (selectedShop) {
        return (
            <AdminProductsFlow
                products={products}
                inventory={inventory}
                onLogout={handleShopDone}
            />
        );
    }
    if (shop && selectedProduct && selectedCategory && checkout) {
        if(paymentTerminalPresent) {
            return (
                <VendView
                    inventory={inventory}
                    product={selectedProduct}
                    onDone={handleVendDone}
                />
            );
        }else {
            return (
                <VendInShop
                    shop={shop}
                    inventory={inventory}
                    category={selectedCategory}
                    product={selectedProduct}
                    onDone={handleVendDone}
                />
            );
        }
    }
    if (selectedCategory) {
        if (selectedProduct) {
            return (
                <ProductView
                    inventory={inventory}
                    category={selectedCategory}
                    product={selectedProduct}
                    onVend={handleVendProduct}
                    onBack={handleProductBack}
                />
            );
        }
        const filtered = products.filter((p) => p.category === selectedCategory.id);
        return (
            <ProductsView
                category={selectedCategory}
                products={filtered}
                onProduct={setSelectedProduct}
                onBack={handleCategoryBack}
            />
        );
    }

    return (
        <CategoriesView
            categories={categories}
            onCategory={setSelectedCategory}
            onPickupOrder={handlePickupOrder}
        />
    );
}
